import { notification } from "antd";

import axios from "axios";

import { Api } from "./index";

import { API_URL, ENV } from "../settings"

import { LoggedUser } from "../context/channel";
import { t } from "i18next";
import { FileUpload } from "../interfaces/types";
import { WhiteLabel } from "../interfaces/university/whitelabel";

const mode: "cors" | "navigate" | "no-cors" | "same-origin" | undefined =
  "cors";
const credentials: "same-origin" | "include" | "omit" | undefined = "include";


let config: any = {
  withCredentials: true,
  headers: {
    "content-type": "multipart/form-data"
  },
};

if (ENV === "staging") {
  config.headers["Authorization"] = "Basic ZXZlbnRpYWxzOnczYjFuNHIhQCM=";
  config.credentials = credentials;
  config.mode = mode;
}


class DashboardApi extends Api {
  async listGroups(owner: string | undefined) {
    const response = await this.axios.get(`/${owner}/groups/`, config);
    return response.data;
  }

  async postData(
    channel: string | undefined,
    data: any,
    files: FileUpload[] = []
  ) {
    const formData = new FormData();

    for (var key in data) {
      formData.append(key, data[key]);
    }
    for (let f of files) {
      formData.append(f.field, f.data, f.name);
    }

    try {
      const response = await this.axios.post(
        `/${channel}/customization/`,
        formData,
        config
      );

      notification.success({
        message: t('dashboard.success.title.saveWL'),
        description:
          t('dashboard.success.text.saveWL'),
      });

      return response.data;
    } catch (err: any) {
      let errorMessage;

      if (err.response && err.response.data && "message" in err.response.data) {
        errorMessage = err.response.data.message;
        notification.error({
          message: t('dashboard.error.saveWL'),
          description: errorMessage,
        });
      } else {
        errorMessage = t('dashboard.unexpectedError');
        notification.error({
          message: t('dashboard.error.saveWL'),
          description: errorMessage,
        });
      }
    }
  }

  async publishWhitelabel(channel: any) {
    try {
      config.headers = {
        "content-type": "application/json",
      };
      const response = await this.axios.post(
        `/${channel}/publish/`,
        {},
        config
      );
      return response.data;
    } catch (err: any) {
      let errorMessage;

      if (err.response && err.response.data && "message" in err.response.data) {
        errorMessage = err.response.data.message;
        notification.error({
          message: t('dashboard.error.publishWL'),
          description: errorMessage,
        });
      } else {
        errorMessage = t('dashboard.unexpectedError');
        notification.error({
          message: t('dashboard.error.publishWL'),
          description: errorMessage,
        });
      }
    }
  }

  async updateActiveGroupsId(owner: string | undefined) {
    try {
      if (owner) {
        const response = await this.axios.post(
          `/${owner}/customization/`,
          {},
          config
        );
        return response.data;
      }
    } catch (err: any) {
      let errorMessage;

      if (err.response && err.response.data && "message" in err.response.data) {
        errorMessage = err.response.data.message;
        notification.error({
          message: t('dashboard.error.saveWL'),
          description: errorMessage,
        });
      } else {
        errorMessage = t('dashboard.unexpectedError');
        notification.error({
          message: t('dashboard.error.saveWL'),
          description: errorMessage,
        });
      }
    }
  }

  async listWhiteLabels(): Promise<WhiteLabel[] | null> {
    try {
      const response = await this.axios.get(`/list/`, config);
      return response.data.whitelabels;
    } catch (err: any) {
      console.info(err);
      return null;
    }
  }

  async createWhiteLabel(name: string, type: string): Promise<Object | string> {
    try {
      const formData = new FormData();

      formData.append("name", name);
      formData.append("kind", type);

      const response = await this.axios.post(`/create/`, formData, config);

      return response;
    } catch (err: any) {
      let errorMessage;

      if (err.response && err.response.data && "message" in err.response.data) {
        errorMessage = err.response.data.message;
      } else {
        errorMessage = t('dashboard.unexpectedError');
      }

      return errorMessage;
    }
  }

  async deleteBanner(
    kind: string,
    channel: string
  ): Promise<Object | string> {
    try {
      config.headers = {
        "content-type": "application/json",
      };

      const response = await this.axios.post(`/${channel}/banners/${kind}/`, {}, config);

      return response;
    } catch (err: any) {
      let errorMessage;

      if (err.response && err.response.data && "message" in err.response.data) {
        errorMessage = err.response.data.message;
      } else {
        errorMessage = t('dashboard.unexpectedError');
      }

      return errorMessage;
    }
  }

  async getAuthentication(): Promise<LoggedUser | string> {
    try {
      config.headers = {
        "content-type": "application/json",
      };

      const user = await axios.post(`${API_URL}/auth/`, {}, config);

      return user.data

    } catch (err: any) {
      let errorMessage;

      if (err.response && err.response.data && "message" in err.response.data) {
        errorMessage = err.response.data.message;
      } else {
        errorMessage = t('dashboard.unexpectedError');
      }

      return errorMessage;
    }
  }


  async editStatus(channel: any, active: boolean): Promise<Object | string> {
    try {
      config.headers = {
        "content-type": "application/json",
      };

      return await this.axios.post(`${API_URL}/api/whitelabel/${channel.slug}/edit-status/?status=${active ? "activate" : "disable"
        }`,
        {},
        config);

    } catch (err: any) {
      let errorMessage;

      if (err.response && err.response.data && "message" in err.response.data) {
        errorMessage = err.response.data.message;
      } else {
        errorMessage = t('dashboard.unexpectedError');
      }

      return errorMessage;
    }
  }

  async deleteWhiteLabel(channel: any): Promise<Object | string> {
    try {
      config.headers = {
        "content-type": "application/json",
      };

      return await this.axios.post(
        `${API_URL}/api/whitelabel/${channel?.slug}/delete/`,
        {},
        config
      );

    } catch (err: any) {
      let errorMessage;

      if (err.response && err.response.data && "message" in err.response.data) {
        errorMessage = err.response.data.message;
      } else {
        errorMessage = t('dashboard.unexpectedError');
      }

      return errorMessage;
    }
  }
}

export const dashboardApi = new DashboardApi();
